import React, { useState, useRef, useEffect } from 'react';
import { useMandantStore } from '../../store/mandantStore';
import type { MandantConfig } from '../../store/mandantStore';
import type { MandantID } from '../../types/branded';

interface Props {
  collapsed?: boolean;
}

export function FirmenSwitcher({ collapsed = false }: Props): React.JSX.Element {
  const { mandanten, aktiverMandant, setAktiverMandant } = useMandantStore();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const aktiver = mandanten.find(m => m.id === aktiverMandant) ?? null;
  const isAlle  = aktiverMandant === null;

  useEffect(() => {
    const handleClick = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => { document.removeEventListener('mousedown', handleClick); };
  }, []);

  const handleSelect = (m: MandantConfig): void => {
    setAktiverMandant(m.id as MandantID);
    setOpen(false);
  };

  const handleAlle = (): void => {
    setAktiverMandant(null);
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => { setOpen(v => !v); }}
        className="flex items-center gap-2 w-full px-3 py-2 rounded-lg hover:bg-gray-100
                   dark:hover:bg-gray-800 transition-colors text-left"
      >
        {/* Farbpunkt / Alle-Icon */}
        {isAlle ? (
          <span className="w-3 h-3 rounded-full flex-shrink-0 bg-gradient-to-br from-blue-400 via-green-400 to-red-400 ring-2 ring-white dark:ring-gray-900" />
        ) : (
          <span
            className="w-3 h-3 rounded-full flex-shrink-0 ring-2 ring-white dark:ring-gray-900"
            style={{ backgroundColor: aktiver?.farbe ?? '#888' }}
          />
        )}
        {!collapsed && (
          <>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                {isAlle ? 'Alle' : (aktiver?.kurz_code ?? '—')}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                {isAlle ? 'Alle Mandanten' : (aktiver?.name ?? '')}
              </p>
            </div>
            <svg className={`w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`}
              fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </>
        )}
      </button>

      {open && (
        <div className="absolute left-0 top-full mt-1 w-64 bg-white dark:bg-gray-900
                        rounded-xl border border-gray-200 dark:border-gray-700 shadow-lg z-50
                        animate-fade-in overflow-hidden">
          <div className="px-3 py-2 border-b border-gray-100 dark:border-gray-800">
            <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">Mandanten</p>
          </div>
          {/* Alle Mandanten */}
          <button
            onClick={handleAlle}
            className={`flex items-center gap-3 w-full px-3 py-2.5 text-left
                       hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors border-b border-gray-100 dark:border-gray-800
                       ${isAlle ? 'bg-blue-50 dark:bg-blue-950/30' : ''}`}
          >
            <span className="w-3 h-3 rounded-full flex-shrink-0 bg-gradient-to-br from-blue-400 via-green-400 to-red-400" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Alle Mandanten</p>
              <p className="text-xs text-gray-400">Keine Filterung</p>
            </div>
            {isAlle && (
              <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd" />
              </svg>
            )}
          </button>
          {mandanten.filter(m => m.aktiv).map(m => (
            <button
              key={m.id}
              onClick={() => { handleSelect(m); }}
              className={`flex items-center gap-3 w-full px-3 py-2.5 text-left
                         hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors
                         ${m.id === aktiverMandant ? 'bg-blue-50 dark:bg-blue-950/30' : ''}`}
            >
              <span
                className="w-3 h-3 rounded-full flex-shrink-0"
                style={{ backgroundColor: m.farbe }}
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{m.name}</p>
                <p className="text-xs text-gray-400">{m.kurz_code}</p>
              </div>
              {m.id === aktiverMandant && (
                <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd" />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
